import { StatusChipColor } from "@/components/common/fields/StatusChipField";
import { ClaimItemStatus, CommonStatus, PolicyStatusType, StoplossClaimStatusType, TaskStatus } from "@/types";
import { ColorType } from "@/types/shared/uiComponents";

export type StatusColor = ClaimItemStatus | PolicyStatusType | StoplossClaimStatusType | TaskStatus;

/**
 * Maps status strings to StatusChipColor values.
 * Centralized logic for consistent status coloring across the app.
 *
 * Used by:
 * - StatusChipField (for React Admin record contexts)
 * - PageHeader (for status chips with edit dropdowns)
 * - Any component that needs status-based coloring
 */
export const getStatusColor = (status?: StatusColor): StatusChipColor => {
  if (!status) return ColorType.Default;

  switch (status.toLowerCase()) {
    // Success states
    case CommonStatus.ACTIVE:
    case CommonStatus.PAID:
    case CommonStatus.CLOSED:
    case CommonStatus.COMPLETED:
    case CommonStatus.REIMBURSED:
    case CommonStatus.PARTIALLY_REIMBURSED:
      return ColorType.Success;

    // Warning states
    case CommonStatus.PENDING:
    case CommonStatus.PENDED:
    case CommonStatus.IN_PROGRESS:
    case CommonStatus.REVIEW_REQUIRED:
    case CommonStatus.IN_PROCESS:
      return ColorType.Warning;

    // Neutral/inactive states
    case CommonStatus.INACTIVE:
    case CommonStatus.DISABLED:
    case CommonStatus.DRAFT:
    case CommonStatus.EXPIRED:
      return ColorType.Default;

    // Error states
    case CommonStatus.DENIED:
    case CommonStatus.SUSPENDED:
    case CommonStatus.CANCELLED:
    case CommonStatus.FAILED:
    case CommonStatus.ERROR:
    case CommonStatus.PROCESSING_FAILED:
    case CommonStatus.UPLOAD_FAILED:
    case CommonStatus.LOADING_FAILED:
      return ColorType.Error;

    // Info states
    case CommonStatus.OPEN:
    case CommonStatus.APPROVED:
    case CommonStatus.PROCESSING:
    case CommonStatus.NEW:
    case CommonStatus.SUBMITTED:
      return ColorType.Info;

    default:
      return ColorType.Default;
  }
};
